import jsQR from 'jsqr';

/** Options for createImageBitmap to get consistent pixel data across Mac (M2) and Windows. */
const BITMAP_OPTS = {
  imageOrientation: 'from-image',
  // Avoid Mac Display P3 conversion so getImageData matches Windows sRGB.
  colorSpaceConversion: 'none',
};

async function createImageBitmapCrossPlatform(file) {
  try {
    return await createImageBitmap(file, BITMAP_OPTS);
  } catch {
    return await createImageBitmap(file);
  }
}

/**
 * Try native BarcodeDetector API (same engine bank apps use, hardware-accelerated).
 * Available on Chrome/Edge 83+, Android WebView, and recent Safari.
 * @param {File|Blob} file
 * @returns {Promise<string|null>}
 */
async function tryNativeDetector(file) {
  if (typeof window.BarcodeDetector === 'undefined') return null;

  try {
    const bitmap = await createImageBitmapCrossPlatform(file);
    const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    const results = await detector.detect(bitmap);
    bitmap.close();
    if (results.length > 0) return results[0].rawValue;
  } catch {
    // not supported or detection failed
  }
  return null;
}

/**
 * Load file into a canvas, returning context and image for jsQR fallback.
 * Uses createImageBitmap with cross-platform options so Mac M2 and Windows give same pixels.
 * @param {File} file
 * @returns {Promise<{canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D}>}
 */
async function loadToCanvas(file) {
  const bitmap = await createImageBitmapCrossPlatform(file);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  // willReadFrequently: true → consistent CPU readback on M2 (avoids GPU canvas differences).
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();
  return { canvas, ctx };
}

function tryJsQR(imageData) {
  const result = jsQR(imageData.data, imageData.width, imageData.height, {
    inversionAttempts: 'attemptBoth',
  });
  return result ? result.data : null;
}

/**
 * Adaptive threshold binarization (similar to OpenCV adaptiveThreshold).
 * Uses a local mean in a blockSize window. Much better than a global threshold
 * for images with uneven lighting or patterned backgrounds (e.g. bank slips).
 */
function adaptiveBinarize(imageData, blockSize = 25, C = 10) {
  const { width, height, data } = imageData;
  const gray = new Float32Array(width * height);
  for (let i = 0; i < gray.length; i++) {
    gray[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  }

  const half = (blockSize - 1) >> 1;

  // Integral image for O(1) mean lookup per pixel
  const integral = new Float64Array((width + 1) * (height + 1));
  for (let y = 0; y < height; y++) {
    let rowSum = 0;
    for (let x = 0; x < width; x++) {
      rowSum += gray[y * width + x];
      integral[(y + 1) * (width + 1) + (x + 1)] =
        rowSum + integral[y * (width + 1) + (x + 1)];
    }
  }

  const out = new Uint8ClampedArray(data.length);
  const W1 = width + 1;
  for (let y = 0; y < height; y++) {
    const y1 = Math.max(0, y - half);
    const y2 = Math.min(height - 1, y + half);
    for (let x = 0; x < width; x++) {
      const x1 = Math.max(0, x - half);
      const x2 = Math.min(width - 1, x + half);
      const area = (y2 - y1 + 1) * (x2 - x1 + 1);
      const sum =
        integral[(y2 + 1) * W1 + (x2 + 1)] -
        integral[y1 * W1 + (x2 + 1)] -
        integral[(y2 + 1) * W1 + x1] +
        integral[y1 * W1 + x1];
      const mean = sum / area;
      const v = gray[y * width + x] < mean - C ? 0 : 255;
      const idx = (y * width + x) * 4;
      out[idx] = out[idx + 1] = out[idx + 2] = v;
      out[idx + 3] = 255;
    }
  }
  return new ImageData(out, width, height);
}

/**
 * Read QR code data from an image file.
 *
 * Strategy (in order):
 *  1. Native BarcodeDetector — same engine as bank / camera apps, fast & accurate.
 *  2. jsQR on original pixels.
 *  3. jsQR on adaptive-binarized image (handles patterned/gradient backgrounds).
 *
 * @param {File} file
 * @returns {Promise<string>}
 */
const readQrData = async (file) => {
  // 1) Native detector — fast, hardware-accelerated, handles noisy backgrounds
  const native = await tryNativeDetector(file);
  if (native) return native;

  // 2) jsQR on raw pixels
  const { canvas, ctx } = await loadToCanvas(file);
  const raw = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const qr1 = tryJsQR(raw);
  if (qr1) return qr1;

  // 3) Adaptive binarization then jsQR (cleans patterned bank-slip backgrounds)
  const bin = adaptiveBinarize(raw);
  const qr2 = tryJsQR(bin);
  if (qr2) return qr2;

  throw new Error('No QR code found');
};

export default readQrData;

